import { useEffect, useMemo, useRef, useState } from 'react'
import { Button, Input } from '@dgshahr/ui-kit'

import IconArrowLeft2Icon from '@/components/common/icons/IconArrowLeft2'
import IconArrowRight2Icon from '@/components/common/icons/IconArrowRight2'
import IconCalendarIcon from '@/components/common/icons/IconCalendar'
import { cn } from '@/utils/helpers/classnames'
import {
  dateToJalali,
  formatGregorianIso,
  formatJalaliDisplay,
  getMonthGrid,
  JALALI_MONTHS,
  JALALI_WEEKDAYS,
  jalaliToDate,
  parseGregorianIso,
  shiftJalaliMonth,
  toPersianDigits
} from '@/utils/functions/jalaliDate'

type ShamsiDatepickerProps = {
  value?: string | null
  onChange: (value: string | null) => void
  labelContent?: string
  placeholder?: string
  name?: string
  id?: string
  required?: boolean
  errorMessage?: string
  disabled?: boolean
}

const ShamsiDatepicker = ({
  value,
  onChange,
  labelContent,
  placeholder = 'تاریخ را انتخاب کنید',
  name,
  id,
  required = false,
  errorMessage,
  disabled = false
}: ShamsiDatepickerProps) => {
  const selectedDate = parseGregorianIso(value)
  const today = dateToJalali(new Date())
  const containerRef = useRef<HTMLDivElement>(null)

  const [isOpen, setIsOpen] = useState(false)
  const [viewYear, setViewYear] = useState(today.jy)
  const [viewMonth, setViewMonth] = useState(today.jm)

  useEffect(() => {
    if (!isOpen) return
    const current = selectedDate ? dateToJalali(selectedDate) : today
    setViewYear(current.jy)
    setViewMonth(current.jm)
  }, [isOpen, value])

  useEffect(() => {
    if (!isOpen) return

    const handlePointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false)
    }

    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('keydown', handleEscape)
    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen])

  const yearOptions = useMemo(() => {
    const start = today.jy - 40
    return Array.from({ length: 81 }, (_, index) => start + index)
  }, [today.jy])

  const days = getMonthGrid(viewYear, viewMonth)
  const selectedJalali = selectedDate ? dateToJalali(selectedDate) : null
  const displayValue = selectedDate ? formatJalaliDisplay(selectedDate) : ''

  const close = () => setIsOpen(false)

  const selectDay = (day: number) => {
    onChange(formatGregorianIso(jalaliToDate(viewYear, viewMonth, day)))
    close()
  }

  const goToToday = () => {
    onChange(formatGregorianIso(new Date()))
    close()
  }

  const clear = () => {
    onChange(null)
    close()
  }

  const goMonth = (amount: number) => {
    const next = shiftJalaliMonth(viewYear, viewMonth, amount)
    setViewYear(next.jy)
    setViewMonth(next.jm)
  }

  return (
    <div ref={containerRef} className="relative">
      <Input
        id={id}
        name={name}
        labelContent={labelContent}
        placeholder={placeholder}
        required={required}
        errorMessage={errorMessage}
        value={displayValue}
        disabled={disabled}
        readOnly
        onClick={(event) => {
          if (disabled) return
          const target = event.target as HTMLElement
          if (target.closest('button')) return
          setIsOpen((open) => !open)
        }}
        onClear={() => onChange(null)}
        leftIcon={<IconCalendarIcon className="size-5" />}
      />

      {isOpen && (
        <div
          className="absolute bottom-full z-50 mb-2 w-[320px] max-w-[calc(100vw-2rem)] rounded-2xl border border-slate-200 bg-white p-3 shadow-lg"
          dir="rtl"
        >
          <div className="flex items-center justify-between gap-2">
          <button
              type="button"
              className="flex size-9 items-center justify-center rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50"
              onClick={() => goMonth(-1)}
              aria-label="ماه قبل"
            >
             <IconArrowRight2Icon className="size-5" />
            </button>

            <div className="flex flex-1 items-center gap-2">
            
              <select
                className="h-9 w-full rounded-lg border border-slate-200 bg-white px-2 text-sm"
                value={viewMonth}
                onChange={(event) => setViewMonth(Number(event.target.value))}
              >
                {JALALI_MONTHS.map((month, index) => (
                  <option key={month} value={index + 1}>
                    {month}
                  </option>
                ))}
              </select>
              <select
                className="h-9 w-full rounded-lg border border-slate-200 bg-white px-2 text-sm"
                value={viewYear}
                onChange={(event) => setViewYear(Number(event.target.value))}
              >
                {yearOptions.map((year) => (
                  <option key={year} value={year}>
                    {toPersianDigits(year)}
                  </option>
                ))}
              </select>
             
            </div>

            <button
              type="button"
              className="flex size-9 items-center justify-center rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50"
              onClick={() => goMonth(1)}
              aria-label="ماه بعد"
            >
              <IconArrowLeft2Icon className="size-5" />
            </button>

          </div>

          <div className="mt-3 grid grid-cols-7 gap-1 text-center text-xs text-slate-400">
            {JALALI_WEEKDAYS.map((day) => (
              <span key={day} className="py-1">
                {day}
              </span>
            ))}
          </div>

          <div className="mt-1 grid grid-cols-7 gap-1">
            {days.map((day, index) => {
              if (!day) {
                return <span key={`empty-${index}`} className="h-9" />
              }

              const isSelected =
                selectedJalali?.jy === viewYear &&
                selectedJalali?.jm === viewMonth &&
                selectedJalali?.jd === day
              const isToday =
                today.jy === viewYear &&
                today.jm === viewMonth &&
                today.jd === day
              const isFriday = index % 7 === 6

              return (
                <button
                  key={day}
                  type="button"
                  onClick={() => selectDay(day)}
                  className={cn(
                    'h-9 rounded-lg text-sm transition-colors',
                    isSelected
                      ? 'bg-primary-500 text-white'
                      : isToday
                        ? 'ring-1 ring-primary-500 text-primary-500'
                        : isFriday
                          ? 'text-red-500 hover:bg-red-50'
                          : 'text-slate-700 hover:bg-slate-100'
                  )}
                >
                  {toPersianDigits(day)}
                </button>
              )
            })}
          </div>

          <div className="mt-3 flex gap-2">
            <Button isFullWidth variant="outline" onClick={clear}>
              پاک کردن
            </Button>
            <Button isFullWidth onClick={goToToday}>
              امروز
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

export default ShamsiDatepicker
